import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from './Modal';
import PayPalSubscription from './PayPalSubscription';

const SubscriptionModal = ({ 
  isOpen, 
  onClose, 
  subscriptionType, // 'single', 'full'
  billingPeriod, // 'monthly', 'annual'
  planData 
}) => {
  const { t } = useTranslation();
  const [selectedProduct, setSelectedProduct] = useState(null);

  // 如果没有必要的数据，不渲染modal
  if (!planData || !subscriptionType || !billingPeriod) {
    return null;
  }

  const products = [
    {
      key: 'traderPro',
      name: 'CryptoTrader Pro',
      title: t('products.traderPro.title'),
      description: t('products.traderPro.subtitle'),
      icon: '📊',
      color: '#3b82f6'
    },
    {
      key: 'tideLab',
      name: 'TideLab',
      title: t('products.tideLab.title'),
      description: t('products.tideLab.subtitle'),
      icon: '🔬',
      color: '#06b6d4'
    },
    {
      key: 'tideAlert',
      name: 'TideAlert',
      title: t('products.tideAlert.title'),
      description: t('products.tideAlert.subtitle'),
      icon: '⚠️',
      color: '#8b5cf6'
    },
    {
      key: 'riskFixer',
      name: 'RiskFixer',
      title: t('products.riskFixer.title'),
      description: t('products.riskFixer.subtitle'),
      icon: '🛡️',
      color: '#f59e0b'
    }
  ];

  const calculateSavings = (monthly, yearly) => {
    const monthlyTotal = parseFloat(monthly.replace('$', '')) * 12;
    const yearlyPrice = parseFloat(yearly.replace('$', ''));
    const savings = monthlyTotal - yearlyPrice;
    const percentage = Math.round((savings / monthlyTotal) * 100);
    return { savings: savings.toFixed(2), percentage };
  };

  const getModalTitle = () => {
    if (subscriptionType === 'single') {
      return billingPeriod === 'monthly' 
        ? t('subscription.modal.singleMonthly') 
        : t('subscription.modal.singleAnnual');
    } else {
      return billingPeriod === 'monthly' 
        ? t('subscription.modal.fullMonthly') 
        : t('subscription.modal.fullAnnual');
    }
  };

  const getCurrentPrice = () => {
    if (!planData) return '$0';
    if (billingPeriod === 'monthly') {
      return planData.monthlyPrice || '$0';
    } else {
      return planData.yearlyPrice || '$0';
    }
  };

  const getPeriodText = () => {
    return billingPeriod === 'monthly' ? t('subscription.monthlyPeriod') : t('subscription.yearlyPeriod');
  };

  const savingsData = billingPeriod === 'annual' && planData && planData.monthlyPrice && planData.yearlyPrice
    ? calculateSavings(planData.monthlyPrice, planData.yearlyPrice)
    : null;

  const getPayPalPlanId = () => {
    // 根据订阅类型和计费周期返回不同的PayPal plan ID
    if (subscriptionType === 'single') {
      return billingPeriod === 'monthly' ? 'P-SINGLE-MONTHLY-PLAN-ID' : 'P-SINGLE-ANNUAL-PLAN-ID';
    } else if (subscriptionType === 'full') {
      return billingPeriod === 'monthly' ? 'P-FULL-MONTHLY-PLAN-ID' : 'P-FULL-ANNUAL-PLAN-ID';
    }
    return 'P-1RC72271P9352551ANDDV4WQ'; // 默认plan ID
  };

  const handleSubscribe = () => {
    if (subscriptionType === 'single' && !selectedProduct) {
      alert(t('subscription.modal.selectProductFirst'));
      return;
    }
    // 这里可以集成实际的支付处理
    console.log('订阅信息:', {
      type: subscriptionType,
      period: billingPeriod,
      product: selectedProduct,
      price: getCurrentPrice(),
      planId: getPayPalPlanId()
    });
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      title={getModalTitle()}
      className="subscription-modal"
    >
      {subscriptionType === 'single' && (
        <div className="product-selection">
          <h4 className="selection-title">{t('subscription.modal.selectProduct')}</h4>
          {products.map((product) => (
            <div
              key={product.key}
              className={`product-option ${selectedProduct === product.key ? 'selected' : ''}`}
              onClick={() => setSelectedProduct(product.key)}
            >
              <div 
                className="product-option-icon" 
                style={{ backgroundColor: product.color }}
              >
                {product.icon}
              </div>
              <div className="product-option-content">
                <div className="product-option-name">{product.name}</div>
                <div className="product-option-title">{product.title}</div>
                <div className="product-option-description">{product.description}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="payment-section">
        <h4 className="payment-title">{t('subscription.modal.paymentInfo')}</h4>
        
        <div className="price-display-modal">
          <span className="price">{getCurrentPrice()}</span>
          <span className="period">{getPeriodText()}</span>
        </div>

        {savingsData && (
          <div className="savings-info">
            <p className="savings-text">
              {t('subscription.saveAmount', { 
                amount: savingsData.savings, 
                percentage: savingsData.percentage 
              })}
            </p>
          </div>
        )}

        {/* 根据计费周期显示不同的支付选项 */}
        {billingPeriod === 'monthly' ? (
          <div className="paypal-section">
            <div className="payment-option-title">{t('subscription.monthlyPayment')}</div>
            <PayPalSubscription 
              planId={getPayPalPlanId()}
              onSuccess={(subscriptionID) => {
                let subscriptionInfo = '';
                
                if (subscriptionType === 'single' && selectedProduct) {
                  const selectedProductData = products.find(p => p.key === selectedProduct);
                  const productName = selectedProductData ? selectedProductData.name : selectedProduct;
                  subscriptionInfo = `${productName} - ${t('subscription.monthlyOption')}`;
                } else {
                  subscriptionInfo = `${subscriptionType === 'full' ? t('subscription.full.title') : ''} - ${t('subscription.monthlyOption')}`;
                }
                
                alert(`${subscriptionInfo} 订阅成功！订阅ID: ${subscriptionID}`);
                onClose();
              }}
              onError={(error) => {
                console.error('订阅失败:', error);
                alert('订阅过程中发生错误，请重试或联系客服。');
              }}
            />
          </div>
        ) : (
          <div className="paypal-section">
            <div className="payment-option-title">{t('subscription.annualPayment')}</div>
            <PayPalSubscription 
              planId={getPayPalPlanId()}
              onSuccess={(subscriptionID) => {
                let subscriptionInfo = '';
                
                if (subscriptionType === 'single' && selectedProduct) {
                  const selectedProductData = products.find(p => p.key === selectedProduct);
                  const productName = selectedProductData ? selectedProductData.name : selectedProduct;
                  subscriptionInfo = `${productName} - ${t('subscription.annualOption')}`;
                } else {
                  subscriptionInfo = `${subscriptionType === 'full' ? t('subscription.full.title') : ''} - ${t('subscription.annualOption')}`;
                }
                
                alert(`${subscriptionInfo} 订阅成功！订阅ID: ${subscriptionID}`);
                onClose();
              }}
              onError={(error) => {
                console.error('订阅失败:', error);
                alert('订阅过程中发生错误，请重试或联系客服。');
              }}
            />
          </div>
        )}

        <div className="modal-actions">
          <button className="modal-button secondary" onClick={onClose}>
            {t('subscription.modal.cancel')}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default SubscriptionModal;